import { Stack, type Theme, Typography, useMediaQuery } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import EnvironmentSliderV2, { type EnvironmentSliderMarker } from '/@/components/environment-slider-v2';
import Spinner from '/@/components/spinner';
import { useZonalYield } from '/@/hooks/crop/use-zonal-yield';
import { useLastCampaign } from '/@/hooks/crop-monitoring/use-last-campaign';
import { _ } from '/@/locale';
import { getCropConfig } from '/@/store/private-data/recommendations/selectors';
import { updateActiveRecommendationCropConfig } from '/@/store/recommendations/actions';
import { getActiveRecommendation } from '/@/store/recommendations/selectors';
import type { Field } from '/@/types/field';
import { useRecommendationEditData } from '/@/views/recommendations/recommendation-aio/hooks';
import ConditionsFields from './conditions-fields';
import LastCampaignCard from './last-campaign-card';
import ZonalYieldCard from './zonal-yield-card';

const MODEL_MARKER_COLOR = '#006A28';
const LAST_CAMPAIGN_MARKER_COLOR = '#006F9B';

const DEFAULT_ENVIRONMENT = {
    percentiles: [],
    min: 3000,
    max: 15000,
    default: 9000,
};

/**
 * "Configurar ambiente" step of the seed recommendation wizard.
 *
 * Reads and writes the same `activeRecommendation` crop config as the legacy
 * recommendation-edit page, and reuses its derivation cascade
 * (`useRecommendationEditData`) so the two stay in sync. Mounted only once a
 * field is selected — the cascade fires requests keyed on the field's location.
 */
function EnvironmentStep({
    field,
    crop = 'corn',
    showConditions = true,
    onLoadingChange,
}: {
    field: Field;
    crop?: 'corn' | 'soy';
    /**
     * The product-first flow asks for the planting date, moisture and climate year on its own
     * opening step, so by the time it reaches this one only the bar is left to set.
     */
    showConditions?: boolean;
    /**
     * Reports the derivation cascade's loading state to the wizard shell, which gates "Siguiente"
     * on it. Lifted through a callback rather than read by the parent: `useRecommendationEditData`
     * rewrites the environment on mount, so a second copy in the parent would fight this one.
     */
    onLoadingChange?: (loading: boolean) => void;
}) {
    const dispatch = useDispatch();
    // Same breakpoint the wizard shell uses to swap its sidebar, so the step switches layout
    // exactly when the surrounding chrome does.
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
    const recommendation = useSelector(getActiveRecommendation);
    const cropConfig = getCropConfig(recommendation, crop);

    const handleChange = (key: string) => {
        return async (value: unknown) => {
            if (['plantingDate', 'environment'].includes(key)) {
                await dispatch(updateActiveRecommendationCropConfig({ key: 'products', value: [] }));
            }
            await dispatch(updateActiveRecommendationCropConfig({ key, value }));
        };
    };

    const { soilMoisturesQuery, environmentsQuery, validPlantingDates, climatePatternChoices, isLoading } =
        useRecommendationEditData({ field, crop, handleChange });

    const { lastCampaign } = useLastCampaign(field?.id);
    // Keyed on the field's own point and the chosen planting date: the value is per department
    // and per planting window, so changing the date in the step above re-resolves it.
    const { zonalYield } = useZonalYield({
        lat: field?.lat,
        lng: field?.lng,
        crop,
        plantingDate: cropConfig?.plantingDate,
    });

    // Cleared on unmount as well as on change: leaving the step mid-request would otherwise
    // leave the shell's flag stuck on and disable "Siguiente" on whatever step comes next.
    useEffect(() => {
        onLoadingChange?.(isLoading);
        return () => onLoadingChange?.(false);
    }, [isLoading, onLoadingChange]);

    // The slider drags against local state so a drag doesn't write to the store —
    // and therefore doesn't clear the selected products — on every tick.
    const [draftEnvironment, setDraftEnvironment] = useState<number | undefined>(undefined);
    // Drops the draft whenever the store's environment changes — including when the cascade
    // re-defaults it — so the slider never shows a stale drag value.
    // biome-ignore lint/correctness/useExhaustiveDependencies: reset keyed on the store value
    useEffect(() => {
        setDraftEnvironment(undefined);
    }, [cropConfig?.environment]);

    if (!cropConfig) {
        return (
            <Stack alignItems='center' py={6}>
                <Spinner />
            </Stack>
        );
    }

    const environment = environmentsQuery.data || DEFAULT_ENVIRONMENT;
    const soilMoistureChoices = soilMoisturesQuery.data || [];

    const markers: EnvironmentSliderMarker[] = [
        {
            value: environment.default,
            label: _('Sugerido del modelo'),
            color: MODEL_MARKER_COLOR,
            lineHeight: lastCampaign ? 100 : 40,
        },
        ...(lastCampaign
            ? [
                  {
                      value: lastCampaign.averageYield,
                      label: _('Rendimiento última campaña'),
                      color: LAST_CAMPAIGN_MARKER_COLOR,
                      lineHeight: 40,
                  },
              ]
            : []),
    ];

    return (
        <Stack spacing={4} pb={4}>
            {showConditions && (
                <ConditionsFields
                    plantingDate={cropConfig.plantingDate}
                    soilMoisture={cropConfig.soilMoisture}
                    climatePattern={cropConfig.climatePattern}
                    soilMoistureChoices={soilMoistureChoices}
                    climatePatternChoices={climatePatternChoices}
                    validPlantingDates={validPlantingDates}
                    isLoading={isLoading}
                    isInitialLoading={isLoading}
                    onChange={handleChange}
                    crop={crop}
                    field={field}
                />
            )}

            <Stack direction={isMobile ? 'column' : 'row'} spacing={4} alignItems='flex-start'>
                <Stack spacing={4} flexGrow={1} width='100%'>
                    <Stack spacing={0.5}>
                        <Typography variant='subtitle1' fontWeight={700} color='primary'>
                            {_('Ambiente del lote')}
                        </Typography>
                        <Typography variant='body2' color='primary'>
                            {_(
                                'La barra muestra el rendimiento objetivo del lote según nuestro modelo de datos, y define qué híbridos y qué densidad se recomiendan.'
                            )}
                        </Typography>
                    </Stack>
                    <EnvironmentSliderV2
                        percentiles={environment.percentiles}
                        min={environment.min}
                        max={environment.max}
                        value={draftEnvironment ?? cropConfig.environment ?? environment.default}
                        onChange={(_event, value) => setDraftEnvironment(value)}
                        onChangeCommitted={(_event, value) => handleChange('environment')(value)}
                        valueLabelDisplay='on'
                        disabled={isLoading}
                        markers={markers}
                        markersVariant={isMobile ? 'list' : 'annotations'}
                    />
                </Stack>
                <Stack spacing={4} width={isMobile ? '100%' : undefined}>
                    {lastCampaign && (
                        <LastCampaignCard fieldName={field.name} lastCampaign={lastCampaign} compact={isMobile} />
                    )}
                    {/* A field outside the dataset's coverage simply gets no card - never a default. */}
                    {zonalYield && <ZonalYieldCard zonalYield={zonalYield} crop={crop} compact={isMobile} />}
                </Stack>
            </Stack>
        </Stack>
    );
}

export default EnvironmentStep;
